'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["forwardedRef", "referenceResolverContext"];
import * as React from 'react';
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import makeDecoratorDisplayName from 'reference-resolvers/lib/makeDecoratorDisplayName';
import { enforceFunction, enforceReactComponent } from 'reference-resolvers/lib/enforce';
import invariant from 'react-utils/invariant';
import curry from 'transmute/curry';
import map from 'transmute/map';
import forEach from 'transmute/forEach';
import isFunction from 'transmute/isFunction';
import { isAtom, watch, unwatch, deref } from 'atom';
import SymbolObjectsSet from 'reference-resolvers/lib/SymbolObjectSet';
import { isResolved } from 'reference-resolvers/utils';
import { referenceResolverContextPropTypes } from './ReferenceResolversContext';
import withReferenceResolverContext from './withReferenceResolverContext';
import { is } from 'immutable';
import { jsx as _jsx } from "react/jsx-runtime";
const unresolvedSymbol = Symbol('unresolved');
const mapAtoms = (fn, structure) => {
  if (isAtom(structure) || structure === unresolvedSymbol) {
    return fn(structure);
  } else if (isFunction(structure)) {
    return structure;
  } else {
    return map(child => mapAtoms(fn, child), structure);
  }
};
const forEachAtom = (fn, structure) => {
  if (structure === unresolvedSymbol) {
    return;
  }
  if (isAtom(structure)) {
    fn(structure);
  } else if (!isFunction(structure)) {
    forEach(child => forEachAtom(fn, child), structure);
  }
};
const isSimpleObject = object => {
  if (object == null) {
    return false;
  }
  const proto = Object.getPrototypeOf(object);
  return proto === Object.prototype;
};
const getResolved = atomStructure => {
  const unresolved = [];
  const derefGetUnresolved = atom => {
    if (atom === unresolvedSymbol) {
      return unresolved;
    }
    const value = deref(atom);
    if (!isResolved(value)) {
      unresolved.push(value);
    }
    return value;
  };
  return mapAtoms(derefGetUnresolved, atomStructure);
};

/**
 * Accepts a reference object type (checkout reference-resolvers/constants/ReferenceObjectType)
 * and a function to map the resolver to the components props.
 *
 * @param {String} referenceObjectType
 * @param {Function} mapResolverToAtoms
 * @param {React.Component | Function} Component
 * @param {Object} opts
 * @return {React.Component} The decorated component
 */
const ResolveReferences = (mapResolversToAtoms, Component, opts = {}) => {
  enforceFunction('mapResolverToAtoms', mapResolversToAtoms);
  enforceReactComponent('Component', Component);
  const getAtomStructure = (resolvers, props) => {
    const result = mapResolversToAtoms(resolvers, props, unresolvedSymbol);
    invariant(isSimpleObject(result), 'expected `mapResolverToAtoms` to return a simple object, but got `%s`', result);
    return result;
  };
  class WithReferences extends React.Component {
    constructor(...args) {
      super(...args);
      this.handleReferenceUpdate = (nextValue, prevValue) => {
        // WARNING: atoms run watchers on _any_ transition, even if the value
        // doesn't change. We only need to re-render when the value changes.
        //
        // By ignoring same-value transitions, we avoid infinite render loops
        // caused by calls to `byId`, or dynamic creation of custom object
        // resolvers in `mapResolverToProps` helpers. These will run
        // `handleReferenceUpdate` callbacks synchronously on with an unchanged
        // value (though not necessarily a referentially-equal value).
        //
        // Use `Immutable.is` to compare primitive types as well as Immutables,
        // with equality semantics similar to `Object.is`.
        // See https://immutable-js.com/docs/v4.1.0/is()/
        if (!is(nextValue, prevValue)) {
          this.forceUpdate();
        }
      };
      this._lastAtoms = null;
      this._currentAtoms = new SymbolObjectsSet();
    }
    componentWillUnmount() {
      this._currentAtoms.forEach(atom => unwatch(atom, this.handleReferenceUpdate));
    }
    updateAtoms(atoms) {
      this._lastAtoms = this._currentAtoms;
      this._currentAtoms = new SymbolObjectsSet();
      forEachAtom(atom => {
        if (atom === unresolvedSymbol) {
          return;
        }
        if (this._lastAtoms.has(atom)) {
          this._lastAtoms.remove(atom);
        } else {
          watch(atom, this.handleReferenceUpdate);
        }
        this._currentAtoms.add(atom);
      }, atoms);
      this._lastAtoms.forEach(atom => unwatch(atom, this.handleReferenceUpdate));
      this._lastAtoms = null;
    }
    render() {
      const _this$props = this.props,
        {
          forwardedRef
        } = _this$props,
        rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
      const resolvers = this.props.referenceResolverContext || {};
      const atomStructure = getAtomStructure(resolvers, this.props);
      const resolved = getResolved(atomStructure);
      this.updateAtoms(atomStructure);
      return /*#__PURE__*/_jsx(Component, Object.assign({
        ref: forwardedRef
      }, rest, resolved));
    }
  }
  WithReferences.propTypes = Object.assign({}, referenceResolverContextPropTypes, {
    forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
      current: PropTypes.object
    })])
  });
  const ContextConnector = withReferenceResolverContext(WithReferences);
  ContextConnector.displayName = makeDecoratorDisplayName(`ResolveReferences`, Component);
  ContextConnector.WrappedComponent = Component;
  if (opts.forwardRef) {
    const ForwardedRef = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(ContextConnector, Object.assign({}, props, {
      forwardedRef: ref
    })));
    ForwardedRef.WrappedComponent = ContextConnector.WrappedComponent;
    return ForwardedRef;
  }
  return ContextConnector;
};
export default curry(ResolveReferences);